/*
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
*/
import {Point, Rect} from '../geometry';
import type {PublicPoint, PublicRect} from '../geometry';
import type {FlowOrientation} from '../types';
import {link, curveBumpX, line, curveBasis} from 'd3-shape';

//fork addition: the card edge an io port sits on. Left/right are the
//upstream layouts; top/bottom come from the TB/BT flow orientations.
export type AnchorSide = 'left' | 'right' | 'top' | 'bottom';

//fork addition: port edge for an io, from its owner factory's flow
//orientation. Temp (drag ghost) ios have no owner orientation; their
//legacy flip flag decides, exactly as the upstream geometry did.
export function anchorSideFor(
    isInput: boolean,
    orientation?: FlowOrientation,
    isFlipped?: boolean,
): AnchorSide {
    switch(orientation) {
        case 'RL': return isInput ? 'right' : 'left';
        case 'TB': return isInput ? 'top' : 'bottom';
        case 'BT': return isInput ? 'bottom' : 'top';
        default: return isFlipped
            ? (isInput ? 'right' : 'left')
            : (isInput ? 'left' : 'right');
    }
}

const svgLinkBuilderStraight = link(curveBumpX);
const svgLinkBuilderCurved = line().curve(curveBasis);

export class LinkShape {
    public readonly svgLink;
    public readonly middlePoint;

    constructor(svgLink: string, middlePoint: PublicPoint) {
        this.svgLink = svgLink;
        this.middlePoint = middlePoint;
    }
}

export class LinkShapeDescriptor {
    public readonly input;
    public readonly output;
    public readonly inputFlipped;
    public readonly outputFlipped;
    //fork addition: card edge each port anchors to (see anchorSideFor);
    //defaults reproduce the legacy flip-based sides exactly
    public readonly inputSide: AnchorSide;
    public readonly outputSide: AnchorSide;

    constructor(
        input?: PublicRect,
        output?: PublicRect,
        inputFlipped?: boolean,
        outputFlipped?: boolean,
        inputSide?: AnchorSide,
        outputSide?: AnchorSide,
    ) {
        this.input = input || Rect.assign();
        this.output = output || Rect.assign();
        this.inputFlipped = inputFlipped || false;
        this.outputFlipped = outputFlipped || false;
        this.inputSide = inputSide ?? anchorSideFor(true, undefined, this.inputFlipped);
        this.outputSide = outputSide ?? anchorSideFor(false, undefined, this.outputFlipped);
    }

    isEqual(shape: LinkShapeDescriptor) {
        return this.input.isEqual(shape.input)
            && this.output.isEqual(shape.output)
            && (this.inputFlipped === shape.inputFlipped)
            && (this.outputFlipped === shape.outputFlipped)
            && (this.inputSide === shape.inputSide)
            && (this.outputSide === shape.outputSide);
    }

    toString() {
        return `${this.input.toString()}=>${this.output.toString()}`;
    }
}

export class LinkShapeModel {
    public readonly shape;
    public readonly descriptor;

    constructor(shape: LinkShape, descriptor: LinkShapeDescriptor) {
        this.shape = shape;
        this.descriptor = descriptor;
    }
}

export class LinkShapeModelBuilder {
    private readonly descriptor;

    constructor(descriptor: LinkShapeDescriptor) {
        this.descriptor = descriptor;
    }

    buildShape() {
        return new LinkShapeModel(this._buildShape(), this.descriptor);
    }

    _buildShape(): LinkShape {
        const {
            input,
            output,
            inputFlipped,
            outputFlipped,
            inputSide,
            outputSide,
        } = this.descriptor;

        //fork addition: the upstream curve builders assume both ports
        //sit on left/right edges. When a TB/BT orientation puts either
        //port on a top/bottom edge, route through the anchored builder
        //instead; pure left/right links keep the upstream shapes.
        const isHorizontal = (side: AnchorSide) => (side === 'left') || (side === 'right');
        if(!isHorizontal(inputSide) || !isHorizontal(outputSide)) {
            return this._buildShapeAnchored();
        }

        const inputPoint = LinkShapeModelBuilder.calculateLinkOrigin(input, !inputFlipped);
        const outputPoint = LinkShapeModelBuilder.calculateLinkOrigin(output, outputFlipped);

        if(inputFlipped == outputFlipped) {
            return this._buildShapeSameDirection(inputPoint, outputPoint, inputFlipped);
        }
        return this._buildShapeMultiDirection(inputPoint, outputPoint, outputFlipped);
    }

    //fork addition: curve for links whose ports anchor to top/bottom
    //(or mixed) edges — leave the producer port along its outward
    //normal, arrive at the consumer port along its own normal, with a
    //basis-smoothed bend between the two stubs
    private _buildShapeAnchored(): LinkShape {
        const {
            input,
            output,
            inputSide,
            outputSide,
        } = this.descriptor;
        const stub = this.curveBreakingWidth();

        const sourcePoint = LinkShapeModelBuilder.calculateAnchorPoint(output, outputSide);
        const targetPoint = LinkShapeModelBuilder.calculateAnchorPoint(input, inputSide);
        const sourceNormal = LinkShapeModelBuilder.anchorNormal(outputSide);
        const targetNormal = LinkShapeModelBuilder.anchorNormal(inputSide);
        const sourceStub = sourcePoint.offsetBy({x: sourceNormal.x * stub, y: sourceNormal.y * stub});
        const targetStub = targetPoint.offsetBy({x: targetNormal.x * stub, y: targetNormal.y * stub});
        const middlePoint = sourcePoint.middlePoint(targetPoint);

        const svgLink = svgLinkBuilderCurved([
            [sourcePoint.x, sourcePoint.y],
            [sourceStub.x, sourceStub.y],
            [targetStub.x, targetStub.y],
            [targetPoint.x, targetPoint.y],
        ]);
        return new LinkShape(svgLink || '', middlePoint);
    }

    //fork addition: the point a link visually attaches to — the middle
    //of the port's card edge, for any of the four edges
    public static calculateAnchorPoint(rect: PublicRect, side: AnchorSide): PublicPoint {
        switch(side) {
            case 'left': return Point.assign({x: rect.x, y: rect.y + (rect.height / 2)});
            case 'right': return Point.assign({x: rect.x + rect.width, y: rect.y + (rect.height / 2)});
            case 'top': return Point.assign({x: rect.x + (rect.width / 2), y: rect.y});
            case 'bottom': return Point.assign({x: rect.x + (rect.width / 2), y: rect.y + rect.height});
        }
    }

    //fork addition: outward direction of a card edge
    public static anchorNormal(side: AnchorSide): {x: number; y: number} {
        switch(side) {
            case 'left': return {x: -1, y: 0};
            case 'right': return {x: 1, y: 0};
            case 'top': return {x: 0, y: -1};
            case 'bottom': return {x: 0, y: 1};
        }
    }

    private _buildShapeSameDirection(inputPoint: PublicPoint, outputPoint: PublicPoint, isFlipped: boolean) {
        const breakingWidth = this.curveBreakingWidth();
        const sourcePoint = isFlipped ? inputPoint : outputPoint;
        const targetPoint = isFlipped ? outputPoint : inputPoint;
        const distance = targetPoint.distanceTo(sourcePoint);

        //important, that link will run through middle point, even if curved
        //so link connector will use the same algorithm for both
        const middlePoint = sourcePoint.middlePoint(targetPoint);

        if((distance.x > breakingWidth) || (Math.abs(distance.y) < breakingWidth)) {
            const svgLink = svgLinkBuilderStraight({
                source: [sourcePoint?.x || 0, sourcePoint?.y || 0],
                target: [targetPoint?.x || 0, targetPoint?.y || 0],
            });
            return new LinkShape(svgLink || '', middlePoint);
        }

        const sourcePoint2 = sourcePoint.offsetBy({x: breakingWidth, y: 0});
        const targetPoint2 = targetPoint.offsetBy({x: -breakingWidth, y: 0});

        //build a square path then make curve for two segments
        const svgLink1 = svgLinkBuilderCurved([
            [sourcePoint.x, sourcePoint.y],
            [sourcePoint2.x, sourcePoint2.y],
            [sourcePoint2.x, middlePoint.y],
            [middlePoint.x, middlePoint.y],
        ]);
        const svgLink2 = svgLinkBuilderCurved([
            [middlePoint.x, middlePoint.y],
            [targetPoint2.x, middlePoint.y],
            [targetPoint2.x, targetPoint2.y],
            [targetPoint.x, targetPoint.y],
        ]);
        return new LinkShape((svgLink1 || '') + (svgLink2 || ''), middlePoint);
    }

    private _buildShapeMultiDirection(inputPoint: PublicPoint, outputPoint: PublicPoint, leftSide: boolean) {
        const breakingWidth = this.curveBreakingWidth();
        const offsetMultiplier = leftSide ? -1 : 1;

        const inputPoint2 = inputPoint.offsetBy({x: offsetMultiplier * breakingWidth, y: 0});
        const outputPoint2 = outputPoint.offsetBy({x: offsetMultiplier * breakingWidth, y: 0});
        const offsetMax = leftSide ? Math.min(inputPoint2.x, outputPoint2.x) : Math.max(inputPoint2.x, outputPoint2.x);
        const middlePoint = inputPoint2.middlePoint(outputPoint2).assignPoint({x: offsetMax});

        //build a square path then make curve for two segments
        const svgLink1 = svgLinkBuilderCurved([
            [inputPoint.x, inputPoint.y],
            [offsetMax, inputPoint2.y],
            [offsetMax, middlePoint.y],
            [middlePoint.x, middlePoint.y],
        ]);
        const svgLink2 = svgLinkBuilderCurved([
            [middlePoint.x, middlePoint.y],
            [offsetMax, middlePoint.y],
            [offsetMax, outputPoint2.y],
            [outputPoint.x, outputPoint.y],
        ]);
        return new LinkShape((svgLink1 || '') + (svgLink2 || ''), middlePoint);
    }

    private static calculateLinkOrigin(rect: PublicRect, isLeftSide: boolean): PublicPoint {
        if(isLeftSide) {
            return Point.assign({
                x: rect.x,
                y: rect.y + (rect.height / 2),
            });
        }
        return Point.assign({
            x: rect.x + rect.width,
            y: rect.y + (rect.height / 2),
        });
    }
    private curveBreakingWidth() {
        const {
            input,
            output,
        } = this.descriptor;

        const maxHeight = Math.max(input.height, output.height);
        return maxHeight * 1.2;
    }
}
